import React, { useCallback, useState } from 'react';
import { PropsWithChildren, useMemo } from 'react';
import { UserDevicesContext } from './contex';
import { UserDevice } from '../../types/userDevice';
import { useGridT4EboxDevices } from '../../hooks/useUserDevices';

export const UserDevicesProvider = ({ children }: PropsWithChildren) => {
  const obuDevices = useGridT4EboxDevices();
  const [selectedDevices, setSelectedDevices] = useState<UserDevice[]>([]);

  const unactiveDevices = obuDevices.filter((d) => d.status !== 'Active');

  const selectDevice = useCallback(
    (deviceSn: UserDevice['deviceSn']) => {
      const device = unactiveDevices.find((d) => d.deviceSn === deviceSn);
      if (!device) return;

      setSelectedDevices((prev) => [...prev, device]);
    },
    [unactiveDevices]
  );

  const deselectDevice = useCallback(
    (deviceSn: UserDevice['deviceSn']) => {
      setSelectedDevices(selectedDevices.filter((d) => d.deviceSn !== deviceSn));
    },
    [selectedDevices]
  );

  const selectAllDevices = useCallback(() => {
    setSelectedDevices([...unactiveDevices.filter((d) => d.status !== 'Active')]);
  }, [unactiveDevices]);

  const deselectAllDevices = useCallback(() => {
    setSelectedDevices([]);
  }, []);

  const hasSelectedDevices = useCallback(
    (deviceSn: UserDevice['deviceSn']) => {
      return selectedDevices.some((d) => d.deviceSn === deviceSn);
    },
    [selectedDevices]
  );

  const selectedDevicesAll = selectedDevices.length === unactiveDevices.length && unactiveDevices.length > 0;
  const value = useMemo(
    () => ({
      selectDevice,
      deselectDevice,
      selectAllDevices,
      deselectAllDevices,
      hasSelectedDevices,
      selectedDevices,
      selectedDevicesAll,
      userDevices: obuDevices,
    }),
    [
      selectDevice,
      deselectDevice,
      selectAllDevices,
      deselectAllDevices,
      hasSelectedDevices,
      selectedDevices,
      selectedDevicesAll,
      obuDevices,
    ]
  );

  return <UserDevicesContext.Provider value={value}>{children}</UserDevicesContext.Provider>;
};
